<template>
    <page-header>Terms &amp; Conditions</page-header>

    <terms-controls v-model="fetchParams" @apply="applyFilters" @reset="resetFilters">
        <vx-button size="lg" block :href="newTermHref" data-test="new-term-button">
            New Term <font-awesome-icon widthAuto :icon="faCirclePlus" />
        </vx-button>
    </terms-controls>

    <page-loading v-if="loading && !list.length" />

    <vx-box
        v-else-if="!loading && !list.length"
        color="info"
        class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
    >
        No terms yet.
        <vx-button :href="newTermHref" data-test="empty-state-new-term-button">
            New Term <font-awesome-icon widthAuto :icon="faCirclePlus" />
        </vx-button>
    </vx-box>

    <vx-table v-else class="xl:grid-cols-5-auto" rowBreakpoint="xl">
        <vx-table-row isHeader>
            <div>Title</div>
            <div>Status</div>
            <div>Effective At</div>
            <div>Creator</div>
        </vx-table-row>

        <term-row v-for="term in list" :key="term.id" :team-id="props.teamId" :term="term" @delete="destroy" />
    </vx-table>

    <div v-if="list.length && fetchParams.cursor" class="mt-8 flex justify-between w-full gap-10">
        <vx-button
            @click="loadData()"
            color="muted-dark"
            size="xl"
            class="grow-0"
            :loading="loading"
            data-test="load-more-button"
        >
            Load More <font-awesome-icon widthAuto :icon="faSpinner" />
        </vx-button>
    </div>
</template>

<script lang="ts" setup>
import { VxBox, VxButton, VxSwal, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { computed, onMounted } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { adminTermsClient } from '~/services';
import type { Term } from '~/services/adminTermsClient.types';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { throwSwalRequestError } from '~/utils/request';
import TermsControls from './TermsControls.vue';
import TermRow from './TermRow.vue';

const props = defineProps<{
    teamId: number;
}>();

const { list, loading, fetchParams, fetchRecords } = useCursorPagination<Term>();

const newTermHref = computed(() => `/kiosk/teams/${props.teamId}/terms/new`);

const defaultFilters = () => ({
    per_page: 20,
    sort_field: 'id',
    sort_dir: 'desc' as const,
    cursor: null,
    query: '',
});

const loadData = () => {
    fetchRecords(adminTermsClient.getTerms(props.teamId, fetchParams.value));
};

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

onMounted(() => {
    resetFilters();
});

const destroy = (term: Term) => {
    VxSwal.fire({
        title: 'Delete Term',
        text: 'Are you sure? There is no undo.',
        icon: 'danger',
        showCancelButton: true,
        confirmButtonColor: 'danger',
        preConfirm: () =>
            adminTermsClient
                .deleteTerm(props.teamId, term.id)
                .then(() => {
                    list.value = list.value.filter((item) => item.id !== term.id);
                    if (!list.value.length) {
                        resetFilters();
                    }
                })
                .catch(throwSwalRequestError),
    });
};
</script>
